import moment from "moment";

import { Guid } from "@/Common/Models/Guid";
import { LabRequestType } from "@/Common/Models/Patient/LabRequest";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { ConsultationRequestPriorityOption, ExamRequestPriorityOption } from "@/Common/Models/UI/Inputs/DropDownOption";
import { MedicalItemScreeningTest } from "@/Patient/Models/MedicalSummary/MedicalItemScreeningTest";
import { ConsultationRequest } from "@/Portfolio/Models/ConsultationRequest";
import { IMedicalRequest } from "@/Portfolio/Models/IMedicalRequest";
import { LabRequest } from "@/Portfolio/Models/LabRequest";
import { MedicalRequestStatus } from "@/Portfolio/Models/MedicalRequestStatus";

interface IHandleMedicalRequest {

    readonly titleLabel: string;

    readonly signPopupTitleLabel: string;

    readonly unableToModifyWarningLabel: string;

    readonly statusSectionTitleLabel: string;

    readonly invalidateWarningLabel: string;

    readonly deleteWarningLabel: string;

    readonly modifyRequestFromPatientFileWarningLabel: string;

    createModel(employee: EmployeeUIModel, encounterId: string, labRequestType?: LabRequestType, isVisibleInCumulativePatientProfile?: boolean): IMedicalRequest;

    cloneModel(model: IMedicalRequest): IMedicalRequest;

    areMedicalRequestFieldsValid(model: IMedicalRequest): boolean;

    areCareElementFieldsValid(careElement: MedicalItemScreeningTest): boolean;

    areFieldsDifferent(initialModel: IMedicalRequest, comparedWithModel: IMedicalRequest): boolean;
}

abstract class BaseMedicalRequestHandler implements IHandleMedicalRequest {
    public abstract titleLabel: string;
    public abstract signPopupTitleLabel: string;
    public abstract unableToModifyWarningLabel: string;
    public abstract statusSectionTitleLabel: string;
    public abstract invalidateWarningLabel: string;
    public abstract deleteWarningLabel: string;
    public abstract createModel(employee: EmployeeUIModel, encounterId: string, labRequestType?: LabRequestType): IMedicalRequest;
    public abstract cloneModel(model: IMedicalRequest): IMedicalRequest;

    public get modifyRequestFromPatientFileWarningLabel(): string {
        return "ModifyRequestFromPatientFileWarningLabel";
    }

    public areMedicalRequestFieldsValid(model: IMedicalRequest): boolean {
        return !!model.requestingStaffContactFacetId
            && !!model.priorityDropDownId
            && this.isDescriptionFieldValid(model)
            && (IsNullOrWhiteSpace(model.dueDateText) || moment(model.dueDateText, "YYYY-MM-DD", true).isValid());
    }

    private isDescriptionFieldValid(model: IMedicalRequest): boolean {
        return (
            !!model.description
            || (
                cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements
                && model instanceof ConsultationRequest
                && !!model.reason
            )
        );
    }

    public areCareElementFieldsValid(model: MedicalItemScreeningTest): boolean {
        const isRequestingStaffPresentIfRequired = cacheStore.ClinicSettings.EnablePreventiveActivityRequestingStaff ? !!model.model.RequestingStaffContactFacetId : true;

        const isCommonFieldsValid = isRequestingStaffPresentIfRequired && !!model.model.FrequencyTypeDropDownId && !!model.model.StatusLookupId && model.isEndAgeValid();
        if (!isCommonFieldsValid)
            return false;

        return !model.isStatusCompleted() || !!model.model.CompletionDate;
    }

    public areFieldsDifferent(initialModel: IMedicalRequest, compareWithModel: IMedicalRequest): boolean {
        return compareWithModel.dueDate != initialModel.dueDate
            || compareWithModel.priorityDropDownId != initialModel.priorityDropDownId
            || compareWithModel.reason != initialModel.reason
            || compareWithModel.description != initialModel.description
            || compareWithModel.statusLookupId != initialModel.statusLookupId
            || compareWithModel.isVisibleInCPP != initialModel.isVisibleInCPP;
    }
}

class ConsultationRequestHandler extends BaseMedicalRequestHandler {
    public get titleLabel(): string {
        return "ConsultationRequestLabel";
    }

    public get signPopupTitleLabel(): string {
        return "FaxConsultationRequestHeaderLabel";
    }

    public get unableToModifyWarningLabel(): string {
        return "UnableToModifyConsultationRequestLabel";
    }

    public get statusSectionTitleLabel(): string {
        return "ConsulationRequestStatusLabel";
    }

    public get invalidateWarningLabel(): string {
        return "InvalidateConsultationRequestWarningLabel";
    }

    public get deleteWarningLabel(): string {
        return "DeleteConsultationRequestWarningLabel";
    }

    public cloneModel(model: ConsultationRequest): ConsultationRequest {
        return new ConsultationRequest(model);
    }

    public createModel(currentEmployee: EmployeeUIModel, encounterId: Guid, labRequestType?: LabRequestType, isVisibleInCumulativePatientProfile: boolean = true): ConsultationRequest {
        if (!currentEmployee || !encounterId)
            return null;

        const isSupervisorRequired = currentEmployee.shouldHaveSupervisorBasedOnTitle();

        return new ConsultationRequest({
            priorityDropDownId: ConsultationRequestPriorityOption.Normal.model.Id,
            lastUpdatedByContactId: currentEmployee.ContactId,
            isSupervisorRequired: isSupervisorRequired,
            requestingStaffContactFacetId: isSupervisorRequired ? null : currentEmployee.ContactFacetId,
            encounterId: encounterId,
            requestDate: moment().toDate(),
            isVisibleInCPP: isVisibleInCumulativePatientProfile,
            statusLookupId: MedicalRequestStatus.getDefault().LookupID
        });
    }

    public areMedicalRequestFieldsValid(model: ConsultationRequest): boolean {
        return super.areMedicalRequestFieldsValid(model)
            && !!model.speciality;
    }

    public areFieldsDifferent(initialModel: ConsultationRequest, compareWithModel: ConsultationRequest): boolean {
        return super.areFieldsDifferent(initialModel, compareWithModel)
            || compareWithModel.speciality !== initialModel.speciality;
    }
}

class LabRequestHandler extends BaseMedicalRequestHandler {
    public get titleLabel(): string {
        return "ExamRequestLabel";
    }

    public get signPopupTitleLabel(): string {
        return "FaxExamRequestHeaderLabel";
    }

    public get unableToModifyWarningLabel(): string {
        return "UnableToModifyExamRequestLabel";
    }

    public get statusSectionTitleLabel(): string {
        return "RequestStatusLabel";
    }

    public get invalidateWarningLabel(): string {
        return "InvalidateRequestWarningLabel";
    }

    public get deleteWarningLabel(): string {
        return "DeleteRequestWarningLabel";
    }

    public cloneModel(model: LabRequest): LabRequest {
        return new LabRequest(model);
    }

    public createModel(currentEmployee: EmployeeUIModel, encounterId: Guid, labRequestType: LabRequestType = LabRequestType.None, isVisibleInCPP: boolean = true): LabRequest {
        if (!currentEmployee || !encounterId)
            return null;

        const isSupervisorRequired = currentEmployee.shouldHaveSupervisorBasedOnTitle();

        return new LabRequest({
            priorityDropDownId: ExamRequestPriorityOption.Normal.model.Id,
            type: labRequestType,
            lastUpdatedByContactId: currentEmployee.ContactId,
            isSupervisorRequired: isSupervisorRequired,
            requestingStaffContactFacetId: isSupervisorRequired ? null : currentEmployee.ContactFacetId,
            encounterId: encounterId,
            requestDate: moment().toDate(),
            isVisibleInCPP: isVisibleInCPP,
            statusLookupId: MedicalRequestStatus.getDefault().LookupID
        });
    }
}

export { BaseMedicalRequestHandler, ConsultationRequestHandler, IHandleMedicalRequest, LabRequestHandler };
